import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Bell } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { H5 } from '@liveauctioneers/hammer-ui-core/text';
import { Tooltip } from '@liveauctioneers/hammer-ui-core/tooltip';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import * as React from 'react';

import { NotificationPermissions } from '@/enums/notificationPermissions.enum';
import { AlertTypes } from '@/redux/api/alerts';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { openSMSConfirmationModal } from '@/redux/modules/modal';
import { getPermission, requestBrowserPermission } from '@/redux/modules/notification';
import { getSubscribedToBrowserAlerts, getSubscribedToSMSAlerts } from '@/redux/modules/smsAlerts';

type Props = {
    className?: string;
    hideManage?: boolean;
    id?: string;
    messageId: string;
    noSms?: boolean;
    to: string;
    withPopover?: boolean;
};

const ManageButton = ({
    className = '',
    hideManage = false,
    id,
    messageId,
    noSms = false,
    to,
    withPopover = false,
}: Props) => {
    const dispatch = useAppDispatch();
    const hasSMSalerts = useAppSelector(getSubscribedToSMSAlerts);
    const hasBrowserAlerts = useAppSelector(getSubscribedToBrowserAlerts);
    const permission = useAppSelector(getPermission);

    const anchorDomId = React.useMemo(
        () => id ?? `manage-notif-${messageId.replace(/[^a-zA-Z0-9-_]/g, '-')}`,
        [id, messageId]
    );

    const onClick = React.useCallback(() => {
        dispatch(openSMSConfirmationModal(AlertTypes.All));
        if (permission !== NotificationPermissions.Denied) {
            dispatch(requestBrowserPermission());
        }
    }, [dispatch, permission]);

    return hasSMSalerts || hasBrowserAlerts || noSms ? (
        hideManage ? null : (
            <Link href={to}>
                <InlineButton
                    icon={Bell}
                    id={id || messageId}
                    size="md"
                >
                    <FormattedMessage id={messageId} />
                </InlineButton>
            </Link>
        )
    ) : (
        <div className={cn('flex justify-end', className)}>
            {withPopover ? (
                <>
                    <Tooltip
                        anchorSelect={`#${anchorDomId}`}
                        className="z-[13] max-w-[300px] !px-0 !py-0"
                        place="top"
                    >
                        <div className="p-sm">
                            <H5 className="mb-xs">
                                <FormattedMessage id="notifications.enable_notifications_prompt_title" />
                            </H5>
                            <FormattedMessage id="notifications.get_notified" />
                        </div>
                    </Tooltip>
                    <Button
                        iconLeft={Bell}
                        id={anchorDomId}
                        onClick={onClick}
                        size="sm"
                        variant="outlined"
                    >
                        <FormattedMessage id="manage_button_enable_notifications" />
                    </Button>
                </>
            ) : (
                <Button
                    iconLeft={Bell}
                    onClick={onClick}
                    size="sm"
                    variant="outlined"
                >
                    <FormattedMessage id="manage_button_enable_notifications" />
                </Button>
            )}
        </div>
    );
};

export default React.memo<Props>(ManageButton);
